import { useCallback, useEffect, useMemo } from 'react';
import { ControllerFontType, makeStyles } from '@bymason-private/controller-v1-theme-provider';
import { ArtifactModel, ArtifactType, ConfigModel, useGetArtifacts } from '@bymason/controller-api-hooks';
import { useAuth } from '@bymason-private/auth-provider';
import { configContext } from '@bymason/runtime-config';
import { GridSelectionModel, Text } from '@bymason-private/legacy-pattern-components';
import { useToast } from '@bymason-private/controller-shared-widgets';
import SelectArtifact from './select-artifact';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    columnGap: 16,
  },
}));

export default function MediaStep({
  config,
  showSelectSplash,
  showSelectAnimation,
  onSelectAnimation,
  onSelectSplash,
}: MediaStepProps) {
  const classes = useStyles();
  const { authToken = '', customerId } = useAuth();
  const [setToastMessage, setToastSeverity] = useToast();
  const [getArtifacts, artifacts, artifactsError, artifactsBusy] = useGetArtifacts(authToken);

  const splashes = useMemo(() => {
    return artifacts?.filter((art) => {
      return art.type === ArtifactType.Media && art.media.type === 'splash';
    });
  }, [artifacts]);

  const animations = useMemo(() => {
    return artifacts?.filter((art) => {
      return art.type === ArtifactType.Media && art.media.type === 'bootanimation';
    });
  }, [artifacts]);

  const selectedSplash: GridSelectionModel = useMemo(() => {
    if (splashes) {
      const configSplash = config?.media?.splash;
      const splashId = splashes.find(
        (art) => art.name === configSplash?.name && art.version === `${configSplash?.version}`
      )?.composite;
      return splashId ? [splashId] : [];
    }
    return [];
  }, [config, splashes]);

  const selectedAnimation: GridSelectionModel = useMemo(() => {
    if (animations) {
      const configAnimation = config?.media?.bootanimation;
      const animationId = animations.find(
        (art) => art.name === configAnimation?.name && art.version === `${configAnimation?.version}`
      )?.composite;
      return animationId ? [animationId] : [];
    }
    return [];
  }, [config, animations]);

  const handleUploadError = useCallback(
    (message: string) => {
      setToastMessage(message);
      setToastSeverity('error');
    },
    [setToastMessage, setToastSeverity]
  );

  const handleUploadSuccess = useCallback(() => {
    if (customerId) {
      getArtifacts({ customerId, type: ArtifactType.Media });
    }
  }, [customerId, getArtifacts]);

  useEffect(() => {
    if (customerId) {
      getArtifacts({ customerId, type: ArtifactType.Media });
    }
  }, [customerId, getArtifacts]);

  useEffect(() => {
    if (artifactsError) {
      setToastMessage(artifactsError);
      setToastSeverity('error');
    }
  }, [artifactsError, setToastMessage, setToastSeverity]);

  return (
    <div className={classes.root}>
      {showSelectSplash && (
        <SelectArtifact
          header={
            <div>
              <Text variant={ControllerFontType.Heading300B}>Select your boot splash (png).{` `}</Text>
              <Text variant={ControllerFontType.Body400R}>
                The splash file is the static image displayed at the start of the device boot-up experience.
                Upload your own, use our{' '}
                <a
                  href={`${configContext !== 'local' ? '/controller2' : ''}/img/iHeartLivesSplash.png`}
                  download
                >
                  sample file
                </a>{' '}
                or{' '}
                <a
                  href='https://docs.bymason.com/setup-guide#build-and-deploy-your-project-using-the-mason-cli'
                  target='_blank'
                  rel='noreferrer'
                >
                  learn how to make one
                </a>
                .
              </Text>
            </div>
          }
          artifacts={splashes}
          artifactsBusy={artifactsBusy}
          selectionType='splash'
          selected={selectedSplash}
          onSelect={onSelectSplash}
          singleSelect
          onUploadSuccess={handleUploadSuccess}
          onError={handleUploadError}
          showDropzone={!splashes?.length}
        />
      )}

      {showSelectAnimation && (
        <SelectArtifact
          header={
            <div>
              <Text variant={ControllerFontType.Heading300B}>Select your boot animation (zip).{` `}</Text>
              <Text variant={ControllerFontType.Body400R}>
                A boot animation is the loop that shows while the device is booting-up. Upload your file, use
                our{' '}
                <a
                  href='https://github.com/MasonAmerica/Mason-Starter-Pack/raw/master/media/bootanimation.zip'
                  download
                >
                  sample file
                </a>{' '}
                or{' '}
                <a
                  href='https://docs.bymason.com/setup-guide#build-and-deploy-your-project-using-the-mason-cli'
                  target='_blank'
                  rel='noreferrer'
                >
                  learn how to make one
                </a>
                .
              </Text>
            </div>
          }
          artifacts={animations}
          artifactsBusy={artifactsBusy}
          selectionType='bootanimation'
          selected={selectedAnimation}
          onSelect={onSelectAnimation}
          singleSelect
          onUploadSuccess={handleUploadSuccess}
          onError={handleUploadError}
          showDropzone={!animations?.length}
        />
      )}
    </div>
  );
}

interface MediaStepProps {
  config: ConfigModel;
  showSelectSplash?: boolean;
  showSelectAnimation?: boolean;
  onSelectSplash?: (selectedArtifacts: ArtifactModel[]) => void;
  onSelectAnimation?: (selectedArtifacts: ArtifactModel[]) => void;
}
